import React, { useMemo } from "react"

import { PracticePayerMappingsMode } from "@common-shared/config/practice.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"

import { PAYER_MAPPINGS_CONFIG } from "../../../config/payerMappings.config"
import { SelectTabsComponent } from "../../../elements/selectTabs.component"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeConfigurationSelector } from "../../../store/selectors/selectedPracticeConfiguration.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { practiceConfigurationsUpsert } from "../../../store/thunks/practiceConfigurations.thunks"
import { PayerMappingsInfoNoticeComponent } from "./payerMappingsInfoNotice.component"

export const PayerMappingsModeComponent: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeConfiguration = useAppSelector(
        selectedPracticeConfigurationSelector,
    )

    const canReadPayerMappings = useHasPermissions(
        OneVerifyPermission.readPracticeConfigurations,
        undefined,
        false,
    )

    const canManagePayerMappings = useHasPermissions(
        OneVerifyPermission.managePracticeConfigurations,
        undefined,
        false,
    )

    const isLoading = useMemo(
        () => RuntimeHelper.isPracticeConfigurationsUpsertLoading(),
        [runtimeState.isLoading],
    )

    const selectedPracticePayerMappingsMode =
        selectedPracticeConfiguration?.payerMappingsMode

    const onModeChange = (newMode: PracticePayerMappingsMode) => {
        if (
            !selectedPracticePayerMappingsMode ||
            newMode === selectedPracticePayerMappingsMode ||
            !canManagePayerMappings ||
            !selectedPracticeRole?.practice
        ) {
            return
        }

        const modeLabel =
            PAYER_MAPPINGS_CONFIG.payerMappingsModeMapping[newMode]

        ConfirmationHelper.enableConfirmation(
            () => {
                dispatch(
                    practiceConfigurationsUpsert({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            payerMappingsMode: newMode,
                        },
                    }),
                )
            },
            `Switch to ${modeLabel} mode?`,
            [
                PAYER_MAPPINGS_CONFIG.payerMappingsModeDescription[newMode],
                <PayerMappingsInfoNoticeComponent subject="Mapping mode" />,
            ],
            undefined,
            `Switch to ${modeLabel}`,
            undefined,
            undefined,
            {
                isLeftAligned: true,
                width: 600,
            },
        )
    }

    if (
        !(canReadPayerMappings || canManagePayerMappings) ||
        !selectedPracticePayerMappingsMode
    ) {
        return null
    }

    return (
        <div
            data-produktly="payer-mappings-mode"
            className="bg-brand-warmLight025 p-[24px] rounded-[8px]"
        >
            <PrimaryText typography="h5">Payer Mappings Mode</PrimaryText>

            <div className="flex gap-[24px]">
                <PrimaryText className="mt-[8px] opacity-75" typography="text">
                    {
                        PAYER_MAPPINGS_CONFIG.payerMappingsModeDescription[
                            selectedPracticePayerMappingsMode
                        ]
                    }
                </PrimaryText>

                <SelectTabsComponent
                    ariaLabel="Payer mapping mode"
                    items={PAYER_MAPPINGS_CONFIG.payerMappingsModeOptions}
                    value={selectedPracticePayerMappingsMode}
                    onChange={onModeChange}
                    disabled={isLoading || !canManagePayerMappings}
                />
            </div>
        </div>
    )
}
